import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { StatusBadge } from '@/components/ui/status-badge';
import { BOOKING_STATUS, Service } from '@/types/service';
import { BOOKING_STATUS_COPY } from '@/constants/booking-status';
import { formatCurrency } from '@/utils/format-currency';
import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React from 'react';
import { View, type ViewStyle } from 'react-native';

type ServiceCardProps = {
  service: Service;
  size?: 'large' | 'small';
};

const cardStyle: ViewStyle = {
  borderCurve: 'continuous',
  boxShadow: '0 1px 2px rgba(23, 17, 10, 0.04), 0 4px 12px rgba(23, 17, 10, 0.05)',
};

export function ServiceCard({ service, size = 'large' }: ServiceCardProps) {
  const enrollmentsCount = service.enrollments?.length || 0;
  const priceLabel = service.defaultPrice ? formatCurrency(service.defaultPrice) : 'A combinar';
  const bookingCopy =
    BOOKING_STATUS_COPY[service.bookingStatus ?? BOOKING_STATUS.CUSTOM_DURATION];

  const handlePress = () => {
    if (!service.id) {
      console.warn('Service ID is missing, cannot navigate');
      return;
    }
    router.push(`/(tabs)/(provider)/services/${service.id}`);
  };

  if (size === 'small') {
    return (
      <PressableScale
        haptic
        onPress={handlePress}
        accessibilityRole="button"
        className="mb-2 w-full flex-row items-center gap-3 rounded-xl border border-neutral-900/5 bg-card px-3 py-3"
        style={cardStyle}
      >
        <View className="h-9 w-9 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name="briefcase-outline" size={16} />
        </View>
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-sm text-card-foreground" numberOfLines={1}>
            {service.name}
          </ThemedText>
          <ThemedText className="text-xs text-neutral-500" numberOfLines={1}>
            {priceLabel} · {enrollmentsCount} {enrollmentsCount === 1 ? 'contrato' : 'contratos'}
          </ThemedText>
        </View>
        <StatusBadge status={{ type: 'service', value: service.isActive }} size="xs" />
        <Ionicons className="text-neutral-300" name="chevron-forward" size={16} />
      </PressableScale>
    );
  }

  return (
    <PressableScale
      haptic
      onPress={handlePress}
      accessibilityRole="button"
      className="mb-3 w-full gap-3 rounded-2xl border border-neutral-900/5 bg-card p-4"
      style={cardStyle}
    >
      <View className="flex-row items-center gap-3">
        <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary-50 dark:bg-primary">
          <Ionicons className="text-primary dark:text-white" name="briefcase-outline" size={18} />
        </View>
        <View className="flex-1 gap-0.5">
          <ThemedText className="font-semibold text-base text-card-foreground" numberOfLines={1}>
            {service.name}
          </ThemedText>
          <ThemedText
            className="font-semibold text-sm text-primary"
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {priceLabel}
          </ThemedText>
        </View>
        <StatusBadge status={{ type: 'service', value: service.isActive }} size="sm" />
      </View>

      {service.description && (
        <ThemedText className="text-sm leading-5 text-neutral-500" numberOfLines={2}>
          {service.description}
        </ThemedText>
      )}

      <View className="flex-row flex-wrap items-center gap-x-4 gap-y-1">
        <View className="flex-row items-center gap-1.5">
          <Ionicons className="text-neutral-400" name={bookingCopy.icon} size={13} />
          <ThemedText className="text-xs text-neutral-500">{bookingCopy.label}</ThemedText>
        </View>
        <View className="flex-row items-center gap-1.5">
          <Ionicons className="text-neutral-400" name="document-text-outline" size={13} />
          <ThemedText className="text-xs text-neutral-500">
            {enrollmentsCount} {enrollmentsCount === 1 ? 'contrato' : 'contratos'}
          </ThemedText>
        </View>
        {service.address && (
          <View className="flex-1 flex-row items-center gap-1.5">
            <Ionicons className="text-neutral-400" name="location-outline" size={13} />
            <ThemedText className="flex-1 text-xs text-neutral-500" numberOfLines={1}>
              {service.address}
            </ThemedText>
          </View>
        )}
      </View>
    </PressableScale>
  );
}
